import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { STATUS } from "@/constants";
import {
  CANDIDATE_SEEDING,
  CANDIDATE_SEEDING_MODES,
  type CandidateSeeding,
  type NuggetConfig,
  type NuggetConfigDraft,
  type NuggetSchedule,
  type NuggetTimeframe,
} from "@/features/data-hub/model/nuggetConfig";
import { apiFetch } from "@/shared/api/apiFetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";

type RequestStatus = "idle" | "loading" | "succeeded" | "failed";

export type NuggetConfigsState = {
  domainId: number | null;
  configs: NuggetConfig[];
  status: RequestStatus;
  error: string | null;
  saveStatus: RequestStatus;
  saveError: string | null;
  deletingConfigId: number | null;
  deleteError: string | null;
};

export const nuggetConfigsInitialState: NuggetConfigsState = {
  domainId: null,
  configs: [],
  status: STATUS.IDLE,
  error: null,
  saveStatus: STATUS.IDLE,
  saveError: null,
  deletingConfigId: null,
  deleteError: null,
};

function isNuggetTimeframe(value: unknown): value is NuggetTimeframe {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  if (row.mode === "absolute") {
    return typeof row.start_date === "string" && typeof row.end_date === "string";
  }
  if (row.mode === "rolling" || row.mode === "calendar") {
    return typeof row.unit === "string" && typeof row.amount === "number";
  }
  return false;
}

function isNuggetSchedule(value: unknown): value is NuggetSchedule {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.kind === "string" &&
    typeof row.interval_count === "number" &&
    typeof row.run_at_time === "string"
  );
}

function isCandidateSeeding(value: unknown): value is CandidateSeeding {
  return CANDIDATE_SEEDING_MODES.some((mode) => mode === value);
}

function isNuggetConfig(value: unknown): value is NuggetConfig {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.id === "number" &&
    typeof row.domain_id === "number" &&
    typeof row.name === "string" &&
    Array.isArray(row.focus_areas) &&
    row.focus_areas.every((item) => typeof item === "string") &&
    typeof row.use_semantic_materiality === "boolean" &&
    // Absent is accepted, not rejected: a backend that has not run the `candidate_seeding`
    // migration yet omits the field, and failing the guard here would reject the whole
    // response and blank the config tab over a field the user may never touch.
    // `normalizeNuggetConfig` fills the default. An unrecognised *value* still fails --
    // that is a real contract mismatch, not a version skew.
    (row.candidate_seeding === undefined || isCandidateSeeding(row.candidate_seeding)) &&
    isNuggetTimeframe(row.timeframe) &&
    Array.isArray(row.schedules) &&
    row.schedules.every(isNuggetSchedule)
  );
}

/** Fill `candidate_seeding` for a row from a backend predating the column. */
function normalizeNuggetConfig(config: NuggetConfig): NuggetConfig {
  // Checked rather than compared against `undefined`: the field is non-optional on the type,
  // so only a runtime check reflects that the value may genuinely be missing on the wire.
  return isCandidateSeeding(config.candidate_seeding)
    ? config
    : { ...config, candidate_seeding: CANDIDATE_SEEDING.OFF };
}

function configsPath(domainId: number): string {
  return `/v1/domains/${domainId}/nugget-configs`;
}

/** `sort_order` is deliberately omitted — the service assigns it from array position. */
function scheduleToBody(schedule: NuggetSchedule): Record<string, unknown> {
  return {
    kind: schedule.kind,
    interval_count: schedule.interval_count,
    day_of_week: schedule.day_of_week ?? null,
    day_of_month: schedule.day_of_month ?? null,
    month_day_mode: schedule.month_day_mode ?? null,
    anchor_date: schedule.anchor_date ?? null,
    run_at_time: schedule.run_at_time,
  };
}

/** `description` is sent as `null` rather than `""` so the column is cleared, not blanked. */
function draftToBody(draft: NuggetConfigDraft): Record<string, unknown> {
  return {
    name: draft.name.trim(),
    description: draft.description.trim() === "" ? null : draft.description.trim(),
    focus_areas: draft.focus_areas,
    use_semantic_materiality: draft.use_semantic_materiality,
    candidate_seeding: draft.candidate_seeding,
    timeframe: draft.timeframe,
    schedules: draft.schedules.map(scheduleToBody),
  };
}

export const fetchNuggetConfigs = createAsyncThunk<
  { domainId: number; configs: NuggetConfig[] },
  number,
  { rejectValue: string }
>("nuggetConfigs/fetch", async (domainId, { rejectWithValue }) => {
  try {
    const res = await apiFetch(configsPath(domainId));
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!Array.isArray(data) || !data.every(isNuggetConfig)) {
      return rejectWithValue("Unexpected response shape");
    }
    return { domainId, configs: data.map(normalizeNuggetConfig) };
  } catch (e) {
    return rejectWithValue(e instanceof Error ? e.message : "Request failed");
  }
});

export type CreateNuggetConfigArgs = {
  domainId: number;
  draft: NuggetConfigDraft;
};

export const createNuggetConfig = createAsyncThunk<
  NuggetConfig,
  CreateNuggetConfigArgs,
  { rejectValue: string }
>("nuggetConfigs/create", async ({ domainId, draft }, { rejectWithValue }) => {
  try {
    const res = await apiFetch(configsPath(domainId), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(draftToBody(draft)),
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isNuggetConfig(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return normalizeNuggetConfig(data);
  } catch (e) {
    return rejectWithValue(e instanceof Error ? e.message : "Request failed");
  }
});

export type UpdateNuggetConfigArgs = {
  domainId: number;
  configId: number;
  draft: NuggetConfigDraft;
};

export const updateNuggetConfig = createAsyncThunk<
  NuggetConfig,
  UpdateNuggetConfigArgs,
  { rejectValue: string }
>("nuggetConfigs/update", async ({ domainId, configId, draft }, { rejectWithValue }) => {
  try {
    const res = await apiFetch(`${configsPath(domainId)}/${configId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      // Every field is sent, so the arrays replace their stored counterparts wholesale — which is
      // how a removed focus area or frequency actually disappears.
      body: JSON.stringify(draftToBody(draft)),
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isNuggetConfig(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return normalizeNuggetConfig(data);
  } catch (e) {
    return rejectWithValue(e instanceof Error ? e.message : "Request failed");
  }
});

export type DeleteNuggetConfigArgs = {
  domainId: number;
  configId: number;
};

export const deleteNuggetConfig = createAsyncThunk<
  number,
  DeleteNuggetConfigArgs,
  { rejectValue: string }
>("nuggetConfigs/delete", async ({ domainId, configId }, { rejectWithValue }) => {
  try {
    const res = await apiFetch(`${configsPath(domainId)}/${configId}`, { method: "DELETE" });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    return configId;
  } catch (e) {
    return rejectWithValue(e instanceof Error ? e.message : "Request failed");
  }
});

function sortByName(configs: NuggetConfig[]): NuggetConfig[] {
  return [...configs].sort((a, b) => a.name.localeCompare(b.name) || a.id - b.id);
}

const nuggetConfigsSlice = createSlice({
  name: "nuggetConfigs",
  initialState: nuggetConfigsInitialState,
  reducers: {
    clearNuggetConfigs: () => nuggetConfigsInitialState,
    clearNuggetConfigSaveError: (state) => {
      state.saveError = null;
      state.saveStatus = STATUS.IDLE;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNuggetConfigs.pending, (state) => {
        state.status = STATUS.LOADING;
        state.error = null;
      })
      .addCase(fetchNuggetConfigs.fulfilled, (state, action) => {
        state.status = STATUS.SUCCEEDED;
        state.domainId = action.payload.domainId;
        state.configs = sortByName(action.payload.configs);
        state.error = null;
      })
      .addCase(fetchNuggetConfigs.rejected, (state, action) => {
        state.status = STATUS.FAILED;
        state.error = action.payload ?? "Request failed";
      })
      .addCase(createNuggetConfig.pending, (state) => {
        state.saveStatus = STATUS.LOADING;
        state.saveError = null;
      })
      .addCase(createNuggetConfig.fulfilled, (state, action) => {
        state.saveStatus = STATUS.SUCCEEDED;
        state.configs = sortByName([...state.configs, action.payload]);
        state.saveError = null;
      })
      .addCase(createNuggetConfig.rejected, (state, action) => {
        state.saveStatus = STATUS.FAILED;
        state.saveError = action.payload ?? "Request failed";
      })
      .addCase(updateNuggetConfig.pending, (state) => {
        state.saveStatus = STATUS.LOADING;
        state.saveError = null;
      })
      .addCase(updateNuggetConfig.fulfilled, (state, action) => {
        state.saveStatus = STATUS.SUCCEEDED;
        state.configs = sortByName(
          state.configs.map((config) =>
            config.id === action.payload.id ? action.payload : config,
          ),
        );
        state.saveError = null;
      })
      .addCase(updateNuggetConfig.rejected, (state, action) => {
        state.saveStatus = STATUS.FAILED;
        state.saveError = action.payload ?? "Request failed";
      })
      .addCase(deleteNuggetConfig.pending, (state, action) => {
        state.deletingConfigId = action.meta.arg.configId;
        state.deleteError = null;
      })
      .addCase(deleteNuggetConfig.fulfilled, (state, action) => {
        state.deletingConfigId = null;
        state.configs = state.configs.filter((config) => config.id !== action.payload);
        state.deleteError = null;
      })
      .addCase(deleteNuggetConfig.rejected, (state, action) => {
        state.deletingConfigId = null;
        state.deleteError = action.payload ?? "Request failed";
      });
  },
});

export const { clearNuggetConfigs, clearNuggetConfigSaveError } = nuggetConfigsSlice.actions;
export const nuggetConfigsReducer = nuggetConfigsSlice.reducer;
